import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// How long after a pushState call before we actually show the loader.
// Keeps cached-chunk navigations from flashing a loader (they commit in < DEBOUNCE_MS).
const DEBOUNCE_MS = 120;
const FADE_MS = 220; // fade-out once the route commits
const SKIP_PATHS = new Set(['/']);

function readBrand() {
  try {
    // Prefer the server-injected brand (set before React boots, no async delay)
    if (window.__HM_BRAND__?.siteName || window.__HM_BRAND__?.logoUrl) {
      return { url: window.__HM_BRAND__.logoUrl || '', name: window.__HM_BRAND__.siteName || '' };
    }
  } catch {}
  try {
    const raw = localStorage.getItem('hm_site_brand');
    if (raw) {
      const b = JSON.parse(raw);
      return { url: b.logoUrl || '', name: b.siteName || '' };
    }
  } catch {}
  return { url: '', name: '' };
}

export default function PageLoader() {
  const location = useLocation();
  const [phase, setPhase] = useState('hidden'); // 'hidden' | 'shown' | 'fading'
  const [brand, setBrand] = useState(readBrand);
  const timers = useRef([]);
  const isFirst = useRef(true);
  const locationKey = `${location.pathname}${location.search}${location.hash}`;

  // Keep brand in sync when settings load or change
  useEffect(() => {
    const sync = () => setBrand(readBrand());
    window.addEventListener('hm-settings-loaded', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('hm-settings-loaded', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // ── Intercept history.pushState (fires synchronously on every <Link> click) ──
  // React 18 won't show the Suspense fallback while keeping the old tree visible,
  // so useLocation() fires AFTER the chunk finishes loading — too late.
  // Patching pushState lets us show the loader the instant the user clicks,
  // before React even starts downloading the new chunk.
  useEffect(() => {
    const origPush = window.history.pushState.bind(window.history);
    window.history.pushState = function (state, title, url) {
      try {
        const dest = new URL(String(url || ''), window.location.origin);
        const isSamePath = dest.pathname === window.location.pathname;

        // Query-only updates on the current page (for example /flights? or /tours?)
        // should not show the global route loader. Those pages manage their own search
        // loading state locally.
        if (!isSamePath && !SKIP_PATHS.has(dest.pathname)) {
          // Debounce: only show if navigation takes > DEBOUNCE_MS (avoids flash for cached chunks)
          timers.current.forEach(clearTimeout);
          timers.current[0] = setTimeout(() => setPhase('shown'), DEBOUNCE_MS);
        }
      } catch {}
      return origPush(state, title, url);
    };
    return () => {window.history.pushState = origPush;};
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Hide loader once React actually commits the new route ──
  // Any committed location change means React has finished rendering the new state.
  // Include search/hash so query-only navigations can also dismiss the loader if needed.
  // At that point we fade the loader out quickly.
  useEffect(() => {
    if (isFirst.current) {isFirst.current = false;return;}
    const currentTimers = timers.current;
    currentTimers.forEach(clearTimeout); // cancel any pending debounced show

    if (SKIP_PATHS.has(location.pathname)) {
      setPhase('hidden');
      return;
    }

    // Fade out fast — content is already rendered beneath us
    setPhase('fading');
    currentTimers[0] = setTimeout(() => setPhase('hidden'), FADE_MS);
    return () => currentTimers.forEach(clearTimeout);
  }, [locationKey, location.pathname]);

  if (phase === 'hidden') return null;

  const isDark = document.documentElement.classList.contains('dark');
  const bg = isDark ? 'rgba(2, 6, 23, 0.97)' : 'rgba(248, 250, 252, 0.97)';
  const textClr = isDark ? '#f8fafc' : '#0f172a';
  const subClr = isDark ? '#64748b' : '#94a3b8';
  const cardBg = isDark ? '#1e293b' : '#ffffff';
  const ringBg = isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0';

  const opacity = phase === 'fading' ? 0 : 1;
  const initial = brand.name ? brand.name.charAt(0).toUpperCase() : 'L';

  return (
    <>
            <style>{`
                @keyframes pl-spin {
                    to { transform: rotate(360deg); }
                }
                @keyframes pl-spin-ccw {
                    to { transform: rotate(-360deg); }
                }
                @keyframes pl-dot-pulse {
                    0%, 100% { opacity: 0.3; transform: scale(0.75); }
                    50% { opacity: 1; transform: scale(1); }
                }
                @keyframes pl-logo-in {
                    from { opacity: 0; transform: scale(0.85); }
                    to   { opacity: 1; transform: scale(1); }
                }
            `}</style>

            <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: bg,
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          opacity,
          transition: `opacity ${FADE_MS}ms ease`,
          pointerEvents: phase === 'fading' ? 'none' : 'all'
        }}>
        
                <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 22,
          animation: 'pl-logo-in 0.3s ease-out both'
        }}>

                    {/* ── Spinner rings + logo ── */}
                    <div style={{ position: 'relative', width: 96, height: 96 }}>

                        {/* Outer fast ring */}
                        <div style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: `3px solid ${ringBg}`,
              borderTopColor: 'var(--hm-primary)',
              borderRightColor: 'var(--hm-primary)',
              animation: 'pl-spin 0.9s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite'
            }} />

                        {/* Inner slow ring (counter-clockwise) */}
                        <div style={{
              position: 'absolute',
              inset: 7,
              borderRadius: '50%',
              border: `2px solid ${ringBg}`,
              borderBottomColor: 'var(--hm-primary)',
              opacity: 0.5,
              animation: 'pl-spin-ccw 1.6s linear infinite'
            }} />

                        {/* Logo circle */}
                        <div style={{
              position: 'absolute',
              inset: 14,
              borderRadius: '50%',
              background: cardBg,
              boxShadow: isDark ?
              '0 0 0 1px rgba(255,255,255,0.05), 0 8px 32px rgba(0,0,0,0.5)' :
              '0 0 0 1px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.10)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden'
            }}>
                            {brand.url ?
              <img
                src={brand.url}
                alt={brand.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  padding: 6
                }} loading="lazy" decoding="async" /> :


              <span style={{
                fontSize: 24,
                fontWeight: 800,
                color: 'var(--hm-primary)',
                lineHeight: 1,
                letterSpacing: '-1px',
                fontFamily: "'Inter', sans-serif",
                userSelect: 'none'
              }}>
                                    {initial}
                                </span>
              }
                        </div>
                    </div>

                    {/* ── Site name + subtitle ── */}
                    <div style={{ textAlign: 'center', lineHeight: 1.3 }}>
                        <p style={{
              margin: 0,
              fontSize: 17,
              fontWeight: 700,
              color: textClr,
              letterSpacing: '-0.3px',
              fontFamily: "'Inter', sans-serif"
            }}>
                            {brand.name}
                        </p>
                        <p style={{
              margin: '5px 0 0',
              fontSize: 11,
              color: subClr,
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              fontFamily: "'Inter', sans-serif",
              fontWeight: 500
            }}>
                            Loading
                        </p>
                    </div>

                    {/* ── Animated dots ── */}
                    <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
                        {[0, 1, 2].map((i) =>
            <div
              key={i}
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: 'var(--hm-primary)',
                animation: `pl-dot-pulse 1.3s ease-in-out ${i * 0.18}s infinite`
              }} />

            )}
                    </div>

                </div>
            </div>
        </>);

}